import React, { useEffect, useState, useCallback } from 'react';
import { adminAPI, plansAPI } from '../../../services/api';

const fmtDate = (s) => (s ? new Date(s).toLocaleString('tr-TR') : '—');

const AdminUsersTab = () => {
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { user, form }

  const reload = useCallback(async (q = '') => {
    setLoading(true);
    setError('');
    try {
      const [usersRes, plansRes] = await Promise.all([
        adminAPI.listUsers(q),
        plansAPI.listAll(),
      ]);
      setUsers(usersRes.data);
      setPlans(plansRes.data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleSearch = (e) => {
    e.preventDefault();
    reload(search.trim());
  };

  const startEdit = (user) => {
    setEditing({
      user,
      form: {
        name: user.name || '',
        role: user.role || 'user',
        status: user.status || 'active',
        plan_id: user.plan_id || '',
        total_minutes: user.total_minutes ?? 0,
        used_minutes: user.used_minutes ?? 0,
      },
    });
  };

  const handleEditChange = (e) => {
    setEditing((prev) => ({ ...prev, form: { ...prev.form, [e.target.name]: e.target.value } }));
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const body = {
        ...editing.form,
        plan_id: editing.form.plan_id === '' ? null : Number(editing.form.plan_id),
        total_minutes: Number(editing.form.total_minutes) || 0,
        used_minutes: Number(editing.form.used_minutes) || 0,
      };
      await adminAPI.updateUser(editing.user.id, body);
      setEditing(null);
      await reload(search.trim());
    } catch (e) {
      alert(e.message);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user ${user.email}? This cannot be undone.`)) return;
    try {
      await adminAPI.deleteUser(user.id);
      await reload(search.trim());
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <div className="admin-section">
      <div className="admin-section-head">
        <h2>Users</h2>
        <form onSubmit={handleSearch} className="admin-search">
          <input
            type="text"
            placeholder="Search name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {loading ? (
        <div className="admin-empty">Loading…</div>
      ) : users.length === 0 ? (
        <div className="admin-empty">No users found.</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Plan</th>
                <th>Minutes (used / total)</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.id}</td>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td>{u.role}</td>
                  <td>{u.status}</td>
                  <td>{u.plan_name || '—'}</td>
                  <td>{u.used_minutes} / {u.total_minutes}</td>
                  <td>{fmtDate(u.created_at)}</td>
                  <td className="admin-actions">
                    <button onClick={() => startEdit(u)}>Edit</button>
                    <button className="danger" onClick={() => handleDelete(u)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="admin-modal-backdrop" onClick={() => setEditing(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit {editing.user.email}</h3>
            <label>Name</label>
            <input name="name" value={editing.form.name} onChange={handleEditChange} />
            <label>Role</label>
            <select name="role" value={editing.form.role} onChange={handleEditChange}>
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
            <label>Status</label>
            <select name="status" value={editing.form.status} onChange={handleEditChange}>
              <option value="active">active</option>
              <option value="inactive">inactive</option>
              <option value="suspended">suspended</option>
            </select>
            <label>Plan</label>
            <select name="plan_id" value={editing.form.plan_id || ''} onChange={handleEditChange}>
              <option value="">— none —</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.slug})</option>
              ))}
            </select>
            <label>Total minutes</label>
            <input type="number" name="total_minutes" value={editing.form.total_minutes} onChange={handleEditChange} />
            <label>Used minutes</label>
            <input type="number" name="used_minutes" value={editing.form.used_minutes} onChange={handleEditChange} />
            <div className="admin-modal-actions">
              <button onClick={() => setEditing(null)}>Cancel</button>
              <button onClick={saveEdit} className="primary">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersTab;
